import { z } from 'zod';
import {
  DEFAULT_STORE_DESIGN,
  storeCornersSchema,
  storeTemplateSchema,
  storeTypefaceSchema,
  type StoreCorners,
  type StoreDesign,
  type StoreTemplate,
  type StoreTheme,
  type StoreTypeface,
} from './storefront.js';
const versionSchema = z.number().int().min(1);

/**
 * A store's design, managed by the platform (adr/0034): the template, the colours (taken from
 * the store's logo or set by hand), the corners and the type, and the opening banner that the AI
 * writes for the store. The store's owner keeps the logo, the favicon and the banner's words in
 * the store admin; the rest is the platform's.
 */

// Lower-cased in place (not a transform), so the same schema also describes a response.
const hexColor = z
  .string()
  .regex(/^#[0-9a-f]{6}$/i)
  .toLowerCase();

/** The three colours the platform chooses; every other shade is derived from them. */
export const storeColorsSchema = z.object({
  /** The main colour: buttons, links, the title's second line. */
  brand: hexColor,
  /** The "indicator" colour: markers, badges, small highlights. */
  accent: hexColor,
  /** The dark colour of dark surfaces (top strip, footer, closing panels). */
  ink: hexColor,
});
export type StoreColors = z.infer<typeof storeColorsSchema>;

/** Where the AI stands with the store's opening banner. */
export const STORE_HERO_STATES = ['NONE', 'QUEUED', 'RUNNING', 'DONE', 'FAILED'] as const;
export const storeHeroStateSchema = z.enum(STORE_HERO_STATES);
export type StoreHeroState = z.infer<typeof storeHeroStateSchema>;

/** Most characters of each banner field (store_homes checks the same). */
export const STORE_HERO_LIMITS = {
  eyebrow: 80,
  title: 120,
  highlight: 80,
  text: 500,
  buttonLabel: 40,
} as const;

export const storeDesignViewSchema = z.object({
  template: storeTemplateSchema,
  corners: storeCornersSchema,
  typeface: storeTypefaceSchema,
  colors: storeColorsSchema,
  /** The store's logo as the platform admin can load it (`GET …/design/logo`), or null. */
  logo: z.object({ mediaId: z.uuid(), contentType: z.string() }).nullable(),
  /** The opening banner as the storefront shows it now (null fields use the storefront's words). */
  hero: z.object({
    eyebrow: z.string().nullable(),
    title: z.string().nullable(),
    highlight: z.string().nullable(),
    text: z.string().nullable(),
    buttonLabel: z.string().nullable(),
  }),
  heroJob: z.object({
    state: storeHeroStateSchema,
    /** When the last job finished or failed. */
    finishedAt: z.iso.datetime().nullable(),
    error: z.string().nullable(),
  }),
  /** The address of the store's home page, to open it beside the screen. */
  storeUrl: z.string().nullable(),
  version: z.number().int(),
});
export type StoreDesignView = z.infer<typeof storeDesignViewSchema>;

export const updateStoreDesignRequestSchema = z.object({
  template: storeTemplateSchema,
  corners: storeCornersSchema,
  typeface: storeTypefaceSchema,
  colors: storeColorsSchema,
  version: versionSchema,
});
export type UpdateStoreDesignRequest = z.infer<typeof updateStoreDesignRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Shades
// ---------------------------------------------------------------------------------------------

function channels(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.slice(1), 16);
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255];
}

function toHex([r, g, b]: readonly number[]): string {
  return `#${[r, g, b]
    .map((channel) =>
      Math.round(Math.min(255, Math.max(0, channel ?? 0)))
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** `hex` moved toward `target` by `amount` (0 keeps it, 1 is the target). */
export function mixColors(hex: string, target: string, amount: number): string {
  const from = channels(hex);
  const to = channels(target);
  return toHex(from.map((channel, index) => channel + ((to[index] ?? 0) - channel) * amount));
}

/** WCAG relative luminance, 0 (black) to 1 (white). */
export function luminance(hex: string): number {
  const [r, g, b] = channels(hex).map((channel) => {
    const value = channel / 255;
    return value <= 0.03928 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG contrast ratio between two colours, 1 to 21. */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number];
  return (light + 0.05) / (dark + 0.05);
}

/**
 * The stored theme from the platform's three colours: the darker and lighter shades of the
 * brand colour, and the dark surfaces' colour. The derived shades are stored too, so the
 * storefront and the flyers read one row as before.
 */
export function themeFromColors(colors: StoreColors): StoreTheme {
  return {
    brand: colors.brand,
    brandDark: mixColors(colors.brand, '#000000', 0.25),
    brandLight: mixColors(colors.brand, '#ffffff', 0.88),
    brandFaint: mixColors(colors.brand, '#ffffff', 0.96),
    accent: colors.accent,
    topBar: colors.ink,
  };
}

/**
 * Every template's own look (adr/0044): the colours, type and corners it was drawn in. Choosing a
 * template in the platform admin starts from these, and a new store gets its template's; the
 * staff may then change the colours and the type.
 */
export const TEMPLATE_DEFAULTS: Record<
  StoreTemplate,
  { colors: StoreColors; typeface: StoreTypeface; corners: StoreCorners }
> = {
  PANORAMA: {
    colors: { brand: '#134e6f', accent: '#f2a541', ink: '#171b21' },
    typeface: 'INDUSTRIAL',
    corners: 'SOFT',
  },
  ATELIER: {
    colors: { brand: '#b4532a', accent: '#5d5f37', ink: '#1c1a17' },
    typeface: 'ASSISTANT',
    corners: 'SHARP',
  },
  DEPOT: {
    colors: { brand: '#111111', accent: '#e8b923', ink: '#111111' },
    typeface: 'KARANTINA',
    corners: 'SHARP',
  },
  FRESH: {
    colors: { brand: '#0f5a43', accent: '#ff6b4a', ink: '#0b3d2e' },
    typeface: 'MODERN',
    corners: 'ROUND',
  },
  PLANNER: {
    colors: { brand: '#8c1c3a', accent: '#e9a23b', ink: '#1f1c1d' },
    typeface: 'HEEBO',
    corners: 'SOFT',
  },
};

/** The platform's three colours of a stored theme and design. */
export function colorsOf(
  theme: StoreTheme,
  design: StoreDesign = DEFAULT_STORE_DESIGN,
): StoreColors {
  return { brand: theme.brand, accent: theme.accent, ink: design.ink };
}
